import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import { displayPhone, normalizePhone } from '@/lib/phone';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session, profile, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'phone'|'email'>('email');
  const [phoneEnabled, setPhoneEnabled] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [phoneStep, setPhoneStep] = useState<'phone'|'code'>('phone');

  useEffect(() => {
    if (!loading && session && profile) {
      navigate(profile.default_landing_page || '/', { replace: true });
    }
  }, [loading, session, profile, navigate]);

  useEffect(() => {
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
    fetch(`${supabaseUrl}/auth/v1/settings`, { headers: { apikey: anonKey } })
      .then((response) => response.ok ? response.json() : null)
      .then((settings) => {
        const enabled = settings?.external?.phone === true;
        setPhoneEnabled(enabled);
        if (enabled) setLoginMethod('phone');
      })
      .catch(() => setPhoneEnabled(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <span className="w-10 h-10 flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin"></i>
          </span>
          <p className="text-sm text-foreground-600">{t('login.checking')}</p>
        </div>
      </div>
    );
  }

  if (session && profile) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loginLoading) return;

    const normalizedEmail = email.trim();

    if (!normalizedEmail || !password) {
      setError(t('login.required'));
      return;
    }

    if (!normalizedEmail.includes('@')) {
      setError(t('login.invalidEmail'));
      return;
    }

    setLoginLoading(true);
    setError('');

    try {
      const result = await Promise.race([
        supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password,
        }),
        new Promise<{ error: { message: string } }>((resolve) =>
          setTimeout(() => resolve({ error: { message: 'timeout' } }), 15000)
        ),
      ]);

      if (result.error) {
        if (result.error.message === 'timeout') {
          setError(t('login.timeout'));
        } else {
          setError(t('login.invalidCredentials'));
        }
      }
    } catch {
      setError(t('login.invalidCredentials'));
    } finally {
      setLoginLoading(false);
    }
  };

  const requestPhoneOtp = async (e:FormEvent<HTMLFormElement>) => {
    e.preventDefault(); if(loginLoading)return;
    const normalized=normalizePhone(phone); if(!normalized){setError('正しい携帯電話番号を入力してください');return;}
    setLoginLoading(true);setError('');
    const {error:otpError}=await supabase.auth.signInWithOtp({phone:normalized,options:{shouldCreateUser:false}});
    if(otpError){setError(otpError.message.toLowerCase().includes('provider')?'SMS認証サービスの設定がまだ完了していません':'この電話番号は登録されていないか、SMSを送信できませんでした');setLoginLoading(false);return;}
    setPhone(normalized);setPhoneStep('code');setLoginLoading(false);
  };

  const verifyPhoneOtp = async (e:FormEvent<HTMLFormElement>) => {
    e.preventDefault();if(loginLoading)return;
    const normalized=normalizePhone(phone);if(!normalized||!/^\d{6}$/.test(otp)){setError('6桁の認証コードを入力してください');return;}
    setLoginLoading(true);setError('');
    const {error:verifyError}=await supabase.auth.verifyOtp({phone:normalized,token:otp,type:'sms'});
    if(verifyError)setError('認証コードが正しくないか、有効期限が切れています');
    setLoginLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#f4f1e9] lg:grid lg:grid-cols-[1.08fr_0.92fr]">
      <section className="relative hidden lg:flex min-h-screen overflow-hidden bg-[#173c31] px-14 py-12 text-white xl:px-20">
        <div className="absolute -left-32 -top-40 h-[420px] w-[420px] rounded-full border border-white/10" />
        <div className="absolute -left-16 -top-24 h-[270px] w-[270px] rounded-full border border-white/10" />
        <div className="absolute -bottom-36 -right-24 h-[440px] w-[440px] rounded-full bg-[#2f7f68]/30 blur-2xl" />
        <div className="absolute right-10 top-16 h-52 w-52 rounded-full border border-[#d4b56a]/25" />

        <div className="relative z-10 flex w-full max-w-3xl flex-col justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-[0_18px_50px_rgba(0,0,0,0.18)]">
              <img src="/assets/peche-et-fleur-logo.jpg" alt="Peche et Fleur" className="h-14 w-14 object-contain" />
            </div>
            <div>
              <p className="text-lg font-extrabold tracking-tight">Peche et Fleur</p>
              <p className="text-xs font-medium uppercase tracking-[0.24em] text-white/60">Staff Portal</p>
            </div>
          </div>

          <div className="my-16 max-w-2xl">
            <p className="mb-5 text-xs font-semibold uppercase tracking-[0.28em] text-[#d4b56a]">{t('login.eyebrow')}</p>
            <h1 className="text-5xl font-semibold leading-[1.08] tracking-[-0.04em] xl:text-6xl">
              {t('login.headline').split('\n').map((line, index) => <span key={line}>{index > 0 && <br />}{line}</span>)}
            </h1>
            <p className="mt-7 max-w-xl text-base leading-8 text-white/65">
              {t('login.description').split('\n').map((line, index) => <span key={line}>{index > 0 && <br />}{line}</span>)}
            </p>
            <div className="mt-10 flex flex-wrap gap-2.5">
              {['Casa Kokoro', "Goldy’s", 'HanaMomo Express', 'HanaMomo Car Rental', 'HanaMomo Car Sales', 'HanaMomo SIM'].map((label) => (
                <span key={label} className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-xs font-medium text-white/75 backdrop-blur">{label}</span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs text-white/45">
            <span className="h-px w-10 bg-[#d4b56a]/70" />
            <span>Hakuba, Nagano</span>
          </div>
        </div>
      </section>

      <main className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-10 sm:px-10 lg:px-14">
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#dbe8df]/70 blur-3xl lg:hidden" />
        <div className="relative z-10 w-full max-w-[440px]">
          <div className="mb-5 flex justify-end"><LanguageSwitcher /></div>
          <div className="mb-8 flex items-center gap-4 lg:hidden">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-[0_12px_30px_rgba(27,60,49,0.12)]">
              <img src="/assets/peche-et-fleur-logo.jpg" alt="Peche et Fleur" className="h-14 w-14 object-contain" />
            </div>
            <div><p className="text-lg font-extrabold tracking-tight text-[#202520]">Peche et Fleur</p><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#39745f]">Staff Portal</p></div>
          </div>

          <div className="mb-8">
            <span className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#e1ebe5] text-[#245744]"><i className="ri-user-smile-line text-xl" /></span>
            <h2 className="text-3xl font-bold tracking-[-0.035em] text-[#202520]">{t('login.greeting')}</h2>
            <p className="mt-2 text-sm leading-6 text-[#686c66]">{t('login.prompt')}</p>
          </div>

          <div className="mb-5 grid grid-cols-2 rounded-xl bg-[#e8e6de] p-1"><button type="button" disabled={!phoneEnabled} onClick={()=>{setLoginMethod('phone');setError('');}} className={`rounded-lg px-3 py-2 text-sm font-semibold ${loginMethod==='phone'?'bg-white text-[#214f3f] shadow-sm':'text-[#686c66]'} disabled:cursor-not-allowed disabled:opacity-45`}>{phoneEnabled ? '電話番号' : '電話番号（準備中）'}</button><button type="button" onClick={()=>{setLoginMethod('email');setError('');}} className={`rounded-lg px-3 py-2 text-sm font-semibold ${loginMethod==='email'?'bg-white text-[#214f3f] shadow-sm':'text-[#686c66]'}`}>メール</button></div>

          <form noValidate onSubmit={loginMethod==='email'?handleSubmit:phoneStep==='phone'?requestPhoneOtp:verifyPhoneOtp} className="space-y-5">
          {loginMethod==='email'?<>
            <div><label htmlFor="email" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-[#4f554f]">{t('login.email')}</label><div className="relative"><span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-[#8a8f88]"><i className="ri-mail-line text-lg" /></span><input id="email" type="email" name="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-14 w-full rounded-2xl border border-[#d8d8cf] bg-white/80 pl-12 pr-4 text-sm text-[#202520] shadow-sm outline-none transition focus:border-[#39745f] focus:ring-4 focus:ring-[#39745f]/10" placeholder="name@petf.jp" /></div></div>
            <div><label htmlFor="password" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-[#4f554f]">{t('login.password')}</label><div className="relative"><span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-[#8a8f88]"><i className="ri-lock-line text-lg" /></span><input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-14 w-full rounded-2xl border border-[#d8d8cf] bg-white/80 pl-12 pr-12 text-sm text-[#202520] shadow-sm outline-none transition focus:border-[#39745f] focus:ring-4 focus:ring-[#39745f]/10" placeholder={t('login.passwordPlaceholder')} /><button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute inset-y-0 right-4 flex items-center text-[#737871]" aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}><i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`} /></button></div></div>
          </>:phoneStep==='phone'?<div><label htmlFor="phone" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-[#4f554f]">携帯電話番号</label><div className="relative"><span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-[#8a8f88]"><i className="ri-smartphone-line text-lg" /></span><input id="phone" inputMode="tel" autoComplete="tel" value={phone} onChange={(e)=>setPhone(e.target.value)} className="h-14 w-full rounded-2xl border border-[#d8d8cf] bg-white/80 pl-12 pr-4 text-sm outline-none focus:border-[#39745f] focus:ring-4 focus:ring-[#39745f]/10" placeholder="090-1234-5678"/></div><p className="mt-2 text-xs text-[#737871]">登録済みの電話番号へ6桁のコードを送ります</p></div>:<div><label htmlFor="otp" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-[#4f554f]">SMS認証コード</label><p className="mb-2 text-xs text-[#737871]">{displayPhone(phone)} に届いた6桁のコード</p><input id="otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} onChange={(e)=>setOtp(e.target.value.replace(/\D/g,''))} className="h-14 w-full rounded-2xl border border-[#d8d8cf] bg-white/80 px-4 text-center text-xl tracking-[0.35em] outline-none focus:border-[#39745f] focus:ring-4 focus:ring-[#39745f]/10" placeholder="123456"/><button type="button" onClick={()=>{setPhoneStep('phone');setOtp('');setError('');}} className="mt-3 text-xs font-semibold text-[#39745f]">電話番号を入力し直す</button></div>}

          {error && (
            <div className="flex items-start gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-px">
                <i className="ri-error-warning-line text-sm text-red-600"></i>
              </span>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loginLoading}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#214f3f] px-4 text-sm font-bold text-white shadow-[0_14px_30px_rgba(33,79,63,0.22)] transition hover:-translate-y-0.5 hover:bg-[#173c31] hover:shadow-[0_18px_36px_rgba(33,79,63,0.28)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loginLoading ? (
              <>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line text-sm animate-spin"></i>
                </span>
                {loginMethod==='phone'?(phoneStep==='phone'?'送信中…':'確認中…'):t('login.submitting')}
              </>
            ) : (
              <>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-login-box-line text-sm"></i>
                </span>
                {loginMethod==='phone'?(phoneStep==='phone'?'認証コードを送信':'ログイン'):t('login.submit')}
              </>
            )}
          </button>
          </form>

          <div className="mt-8 flex items-center justify-between border-t border-[#dad8cf] pt-5 text-[11px] text-[#858982]">
            <span>Peche et Fleur Co., Ltd.</span><span className="flex items-center gap-1.5"><i className="ri-shield-check-line text-[#39745f]" />Secure staff access</span>
          </div>
        </div>
      </main>
    </div>
  );
}
